import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  AlertTriangle, Pill, FileText, Shield, Users, Clock,
  RefreshCw, Building2, ChevronRight, CheckCircle2, XCircle,
  Target, ClipboardCheck, Heart
} from 'lucide-react';

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default', link }) {
  const variants = {
    default: 'bg-card hover:border-primary/30',
    primary: 'bg-primary/5 border-primary/20 hover:border-primary/40',
    success: 'bg-green-500/5 border-green-500/20 hover:border-green-500/40',
    warning: 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40',
    danger: 'bg-red-500/5 border-red-500/20 hover:border-red-500/40',
  };

  const content = (
    <Card className={`${variants[variant]} transition-colors ${link ? 'cursor-pointer' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {Icon && (
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                variant === 'danger' ? 'bg-red-500/10' :
                variant === 'warning' ? 'bg-amber-500/10' :
                variant === 'success' ? 'bg-green-500/10' :
                'bg-primary/10'
              }`}>
                <Icon className={`h-5 w-5 ${
                  variant === 'danger' ? 'text-red-500' :
                  variant === 'warning' ? 'text-amber-500' :
                  variant === 'success' ? 'text-green-500' :
                  'text-primary'
                }`} />
              </div>
            )}
            {link && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

function DetailCard({ title, description, items }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center py-1.5">
            <div className="flex items-center gap-2">
              {item.icon && <item.icon className={`h-4 w-4 ${item.iconColor || 'text-muted-foreground'}`} />}
              <span className="text-sm">{item.label}</span>
            </div>
            <span className={`font-medium ${item.valueColor || ''}`}>{item.value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SeverityBreakdown({ data }) {
  if (!data || Object.keys(data).length === 0) return null;

  const severityColors = {
    CRITICAL: 'bg-red-500',
    HIGH: 'bg-amber-500',
    MEDIUM: 'bg-blue-500',
    LOW: 'bg-slate-400',
  };

  const total = Object.values(data).reduce((sum, count) => sum + count, 0);

  return (
    <div className="space-y-2">
      {Object.entries(data).map(([severity, count]) => {
        const percent = total > 0 ? Math.round((count / total) * 100) : 0;
        return (
          <div key={severity} className="space-y-1">
            <div className="flex justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${severityColors[severity] || 'bg-slate-400'}`} />
                <span>{severity}</span>
              </div>
              <span className="font-medium">{count}</span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className={`h-full ${severityColors[severity] || 'bg-slate-400'}`}
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ClinicalDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, branchRes] = await Promise.all([
        api.get('/kpi/clinical', { params }),
        api.get('/org/branches'),
      ]);
      setKpis(kpiRes.data.data);
      setBranches(branchRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load clinical data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]);

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const details = kpis?.details || {};

  // Calculate critical alerts
  const criticalCount = (kpis?.reportableIncidents || 0) + 
                        (kpis?.participantsHighRisk || 0) + 
                        (details.incidents?.openBySeverity?.CRITICAL || 0);

  return (
    <div className="space-y-6" data-testid="clinical-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Clinical Dashboard</h1>
          <p className="text-muted-foreground">Clinical care and compliance monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px]" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Critical Alert Banner */}
      {criticalCount > 0 && (
        <Card className="border-red-500/50 bg-red-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div className="flex-1">
                <p className="font-medium text-red-700">Critical Clinical Alerts</p>
                <p className="text-sm text-muted-foreground">
                  {kpis?.reportableIncidents > 0 && (
                    <span className="text-red-600">{kpis.reportableIncidents} reportable incidents</span>
                  )}
                  {kpis?.reportableIncidents > 0 && kpis?.participantsHighRisk > 0 && ' · '}
                  {kpis?.participantsHighRisk > 0 && (
                    <span className="text-red-600">{kpis.participantsHighRisk} high-risk participants</span>
                  )}
                  {(kpis?.reportableIncidents > 0 || kpis?.participantsHighRisk > 0) && details.incidents?.openBySeverity?.CRITICAL > 0 && ' · '}
                  {details.incidents?.openBySeverity?.CRITICAL > 0 && (
                    <span className="text-red-600">{details.incidents.openBySeverity.CRITICAL} critical incidents</span>
                  )}
                </p>
              </div>
              <Button variant="destructive" size="sm" asChild>
                <Link to="/compliance/incidents">View Incidents</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary KPIs - Row 1: Incidents */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Incidents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Open Incidents"
            value={kpis?.openIncidents || 0}
            subtitle={`${details.incidents?.thisWeek || 0} this week · ${details.incidents?.participantsAffected || 0} participants`}
            icon={AlertTriangle}
            variant={kpis?.openIncidents > 5 ? 'danger' : kpis?.openIncidents > 0 ? 'warning' : 'success'}
            link="/compliance/incidents"
          />
          <KpiCard
            title="Reportable Incidents"
            value={kpis?.reportableIncidents || 0}
            subtitle="Requiring external notification"
            icon={FileText}
            variant={kpis?.reportableIncidents > 0 ? 'danger' : 'success'}
            link="/compliance/incidents"
          />
          <KpiCard
            title="Participants High Risk"
            value={kpis?.participantsHighRisk || 0}
            subtitle={`of ${details.participants?.active || 0} active participants`}
            icon={Target}
            variant={kpis?.participantsHighRisk > 0 ? 'danger' : 'success'}
          />
          <KpiCard
            title="Staff Acknowledgements Missing"
            value={kpis?.staffAcknowledgementMissing || 0}
            subtitle="Required acknowledgements pending"
            icon={ClipboardCheck}
            variant={kpis?.staffAcknowledgementMissing > 0 ? 'warning' : 'success'}
          />
        </div>
      </div>

      {/* Primary KPIs - Row 2: Medication */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Medication Administration</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Medication Missed"
            value={kpis?.medicationMissed || 0}
            subtitle={`${details.medicationAdministration?.complianceRate || 100}% compliance rate`}
            icon={Pill}
            variant={kpis?.medicationMissed > 5 ? 'danger' : kpis?.medicationMissed > 0 ? 'warning' : 'success'}
          />
          <KpiCard
            title="Medication Refused"
            value={kpis?.medicationRefused || 0}
            subtitle="Patient refused doses"
            icon={XCircle}
            variant={kpis?.medicationRefused > 5 ? 'warning' : 'default'}
          />
          <KpiCard
            title="Risk Assessments Due"
            value={kpis?.riskAssessmentsDue || 0}
            subtitle={`${details.riskAssessments?.upcoming14Days || 0} upcoming in 14 days`}
            icon={Shield}
            variant={kpis?.riskAssessmentsDue > 0 ? 'warning' : 'success'}
          />
          <KpiCard
            title="Care Plans Due"
            value={kpis?.carePlansDue || 0}
            subtitle={`${details.carePlans?.upcoming14Days || 0} upcoming · ${details.carePlans?.totalActive || 0} active`}
            icon={Heart}
            variant={kpis?.carePlansDue > 0 ? 'warning' : 'success'}
          />
        </div>
      </div>

      {/* Medication Compliance Bar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Medication Compliance</CardTitle>
          <CardDescription>
            {details.medicationAdministration?.administered || 0} administered of {details.medicationAdministration?.scheduled || 0} scheduled
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress 
            value={details.medicationAdministration?.complianceRate || 0} 
            className={`h-3 ${
              (details.medicationAdministration?.complianceRate || 0) < 90 ? 'bg-red-200' : 
              (details.medicationAdministration?.complianceRate || 0) < 95 ? 'bg-amber-200' : ''
            }`}
          />
          <div className="flex justify-between mt-2 text-sm">
            <span className="text-muted-foreground">{details.medicationAdministration?.complianceRate || 0}% compliance</span>
            <div className="flex gap-4">
              <span className="text-green-600">{details.medicationAdministration?.administered || 0} given</span>
              <span className="text-red-600">{kpis?.medicationMissed || 0} missed</span>
              <span className="text-amber-600">{kpis?.medicationRefused || 0} refused</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incident Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Open Incidents by Severity</CardTitle>
            <CardDescription>{kpis?.openIncidents || 0} total open</CardDescription>
          </CardHeader>
          <CardContent>
            <SeverityBreakdown data={details.incidents?.openBySeverity} />
            {(!details.incidents?.openBySeverity || Object.keys(details.incidents.openBySeverity).length === 0) && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm">No open incidents</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Risk Assessment Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Risk Assessments by Level</CardTitle>
          </CardHeader>
          <CardContent>
            <SeverityBreakdown data={details.riskAssessments?.byLevel} />
            {(!details.riskAssessments?.byLevel || Object.keys(details.riskAssessments.byLevel).length === 0) && (
              <p className="text-sm text-muted-foreground">No active risk assessments</p>
            )}
          </CardContent>
        </Card>

        {/* Incident Status */}
        <DetailCard
          title="Incident Status"
          items={[
            ...Object.entries(details.incidents?.byStatus || {}).map(([status, count]) => ({
              label: status,
              value: count,
              icon: status === 'CLOSED' ? CheckCircle2 : status === 'OPEN' ? AlertTriangle : Clock,
              iconColor: status === 'CLOSED' ? 'text-green-500' : status === 'OPEN' ? 'text-red-500' : 'text-amber-500',
            })),
            { label: 'Reportable', value: kpis?.reportableIncidents || 0, icon: FileText, iconColor: 'text-red-500', valueColor: kpis?.reportableIncidents > 0 ? 'text-red-600 font-bold' : '' },
          ]}
        />

        {/* Care Plan & Review Status */}
        <DetailCard
          title="Review Status"
          items={[
            { label: 'Care Plans Overdue', value: kpis?.carePlansDue || 0, icon: Heart, iconColor: 'text-amber-500', valueColor: kpis?.carePlansDue > 0 ? 'text-amber-600' : '' },
            { label: 'Care Plans Upcoming (14d)', value: details.carePlans?.upcoming14Days || 0 },
            { label: 'Active Care Plans', value: details.carePlans?.totalActive || 0, icon: CheckCircle2, iconColor: 'text-green-500' },
            { label: 'Risk Assessments Overdue', value: kpis?.riskAssessmentsDue || 0, icon: Shield, iconColor: 'text-amber-500', valueColor: kpis?.riskAssessmentsDue > 0 ? 'text-amber-600' : '' },
            { label: 'Risk Assessments Upcoming (14d)', value: details.riskAssessments?.upcoming14Days || 0 },
          ]}
        />

        {/* Acknowledgements */}
        <DetailCard
          title="Staff Acknowledgements"
          items={[
            { label: 'Missing/Overdue', value: kpis?.staffAcknowledgementMissing || 0, icon: ClipboardCheck, iconColor: 'text-amber-500', valueColor: kpis?.staffAcknowledgementMissing > 0 ? 'text-amber-600' : '' },
            ...Object.entries(details.acknowledgements?.byType || {}).map(([type, count]) => ({
              label: type,
              value: count
            }))
          ]}
        />

        {/* Participants Summary */}
        <DetailCard
          title="Participant Status"
          items={[
            { label: 'Active Participants', value: details.participants?.active || 0, icon: Users },
            { label: 'High Risk', value: kpis?.participantsHighRisk || 0, icon: Target, iconColor: 'text-red-500', valueColor: kpis?.participantsHighRisk > 0 ? 'text-red-600 font-bold' : '' },
            { label: 'With Open Incidents', value: details.participants?.withOpenIncidents || 0, icon: AlertTriangle, iconColor: 'text-amber-500' },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" asChild>
          <Link to="/compliance/incidents">Incidents</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/clinical/medications">Medications</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/participants/list">Participants</Link>
        </Button>
      </div>

      {/* Data Sources & Period */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Data Sources:</span> incidents, medication administration, risk assessments, care plans, acknowledgements
            </div>
            <div>
              Period: {kpis?.period?.dateFrom} to {kpis?.period?.dateTo} · 
              Generated: {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
